// src/pages/customer-panel/subscription/SubscriptionPlanPage.js
// Customer subscription plan management — view current plan and switch plans.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const PLAN_CONFIG = {
    per_hour: {
        label: 'Per Hour',
        icon: <AccessTimeIcon sx={{ fontSize: 32, color: '#3b82f6' }} />,
        color: '#3b82f6',
        bgColor: '#eff6ff',
        description: 'Billed based on the duration of each live agent session.',
        rateLabel: '/hour',
    },
    per_instance: {
        label: 'Per Instance',
        icon: <ConfirmationNumberIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />,
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
        description: 'A fixed charge for each live agent session.',
        rateLabel: '/session',
    },
    per_month: {
        label: 'Per Month',
        icon: <CalendarMonthIcon sx={{ fontSize: 32, color: GREEN_ACCENT }} />,
        color: GREEN_ACCENT,
        bgColor: '#ecfdf5',
        description: 'A flat monthly subscription fee, billed automatically each month.',
        rateLabel: '/month',
    },
};

export default function SubscriptionPlanPage() {
    const [loading, setLoading] = useState(true);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [changingPlan, setChangingPlan] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/customer-subscriptions/available-plans');
            if (res.data?.success) {
                setCurrentSubscription(res.data.data.current_subscription);
                setAvailablePlans(res.data.data.available_plans || []);
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to load subscription plans.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const handleChangePlan = (planType) => {
        setChangingPlan(planType);
        setConfirmOpen(true);
    };

    const handleConfirmChange = async () => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.put('/customer-subscriptions/change-plan', {
                plan_type: changingPlan,
            });
            if (res.data?.success) {
                setSnackbar({ open: true, message: 'Plan changed successfully!', severity: 'success' });
                setConfirmOpen(false);
                setChangingPlan(null);
                fetchPlans();
            } else {
                setSnackbar({ open: true, message: res.data?.message || 'Failed to change plan.', severity: 'error' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to change plan.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const changingPlanConfig = changingPlan ? PLAN_CONFIG[changingPlan] : null;
    const changingPlanData = availablePlans.find(p => p.plan_type === changingPlan);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: GREEN_ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto', fontFamily }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <CardMembershipIcon sx={{ fontSize: 28, color: GREEN_ACCENT }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily, color: '#1e293b' }}>
                    My Subscription
                </Typography>
            </Box>

            {/* Current Plan */}
            {currentSubscription ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 4,
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        background: '#fff',
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: '#64748b', fontFamily, mb: 2, textTransform: 'uppercase', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
                        Current Plan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {PLAN_CONFIG[currentSubscription.plan_type]?.icon}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily, color: '#1e293b' }}>
                                {PLAN_CONFIG[currentSubscription.plan_type]?.label || currentSubscription.plan_type}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', fontFamily }}>
                                ${currentSubscription.rate.toFixed(2)}{PLAN_CONFIG[currentSubscription.plan_type]?.rateLabel}
                            </Typography>
                        </Box>
                        <Chip
                            label={currentSubscription.status === 'active' ? 'Active' : currentSubscription.status}
                            sx={{
                                bgcolor: currentSubscription.status === 'active' ? '#d1fae5' : '#fef3c7',
                                color: currentSubscription.status === 'active' ? '#065f46' : '#92400e',
                                fontWeight: 700,
                                fontFamily,
                                fontSize: 12,
                            }}
                        />
                    </Box>
                    {currentSubscription.plan_type === 'per_month' && currentSubscription.current_period_start && (
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontFamily, mt: 1.5, fontSize: 13 }}>
                            Current period: {new Date(currentSubscription.current_period_start).toLocaleDateString()} — {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Alert severity="info" sx={{ mb: 4, fontFamily, borderRadius: 2 }}>
                    You don't have an active subscription plan.
                </Alert>
            )}

            {/* Available Plans */}
            {availablePlans.length > 0 && (
                <>
                    <Typography variant="subtitle2" sx={{ color: '#64748b', fontFamily, mb: 2, textTransform: 'uppercase', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
                        Available Plans
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
                        {availablePlans.map((plan) => {
                            const config = PLAN_CONFIG[plan.plan_type] || {};
                            const isCurrent = currentSubscription?.plan_type === plan.plan_type;

                            return (
                                <Card
                                    key={plan.plan_type}
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        border: isCurrent ? `2px solid ${config.color || GREEN_ACCENT}` : '1px solid #e2e8f0',
                                        transition: 'all 0.2s',
                                        position: 'relative',
                                        '&:hover': !isCurrent ? { borderColor: config.color || '#94a3b8', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } : {},
                                    }}
                                >
                                    {isCurrent && (
                                        <Box sx={{
                                            position: 'absolute', top: 12, right: 12,
                                            display: 'flex', alignItems: 'center', gap: 0.5,
                                        }}>
                                            <CheckCircleIcon sx={{ fontSize: 18, color: config.color || GREEN_ACCENT }} />
                                            <Typography variant="caption" sx={{ color: config.color || GREEN_ACCENT, fontWeight: 700, fontFamily, fontSize: 11 }}>
                                                CURRENT
                                            </Typography>
                                        </Box>
                                    )}
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{
                                            width: 56, height: 56, borderRadius: 2,
                                            bgcolor: config.bgColor || '#f1f5f9',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mb: 2,
                                        }}>
                                            {config.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily, color: '#1e293b', mb: 0.5 }}>
                                            {config.label || plan.label}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, fontFamily, color: config.color || '#1e293b', mb: 1 }}>
                                            ${(plan.rate || 0).toFixed(2)}
                                            <Typography component="span" variant="body2" sx={{ color: '#94a3b8', fontWeight: 500, fontFamily }}>
                                                {config.rateLabel}
                                            </Typography>
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontFamily, mb: 2.5, minHeight: 40 }}>
                                            {config.description}
                                        </Typography>
                                        {isCurrent ? (
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                disabled
                                                sx={{
                                                    fontFamily, fontWeight: 700, borderRadius: 2,
                                                    textTransform: 'none', fontSize: 14,
                                                    borderColor: '#e2e8f0', color: '#94a3b8',
                                                }}
                                            >
                                                Current Plan
                                            </Button>
                                        ) : (
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                startIcon={<SwapHorizIcon />}
                                                onClick={() => handleChangePlan(plan.plan_type)}
                                                sx={{
                                                    fontFamily, fontWeight: 700, borderRadius: 2,
                                                    textTransform: 'none', fontSize: 14,
                                                    bgcolor: config.color || GREEN_ACCENT,
                                                    '&:hover': { bgcolor: config.color || GREEN_ACCENT, filter: 'brightness(0.9)' },
                                                }}
                                            >
                                                Switch to {config.label}
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                </>
            )}

            {availablePlans.length === 0 && !currentSubscription && (
                <Alert severity="warning" sx={{ fontFamily, borderRadius: 2 }}>
                    No subscription plans are available for your team. Please contact your administrator.
                </Alert>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmOpen}
                onClose={() => { if (!submitting) { setConfirmOpen(false); setChangingPlan(null); } }}
                PaperProps={{ sx: { borderRadius: 3, maxWidth: 420 } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, pb: 1 }}>
                    Change Subscription Plan
                </DialogTitle>
                <DialogContent>
                    {changingPlanConfig && changingPlanData && (
                        <Box>
                            <Typography variant="body2" sx={{ fontFamily, color: '#475569', mb: 2 }}>
                                Are you sure you want to switch to the <strong>{changingPlanConfig.label}</strong> plan
                                at <strong>${(changingPlanData.rate || 0).toFixed(2)}{changingPlanConfig.rateLabel}</strong>?
                            </Typography>
                            {currentSubscription && (
                                <Alert severity="info" sx={{ fontFamily, fontSize: 13, borderRadius: 2 }}>
                                    Your current <strong>{PLAN_CONFIG[currentSubscription.plan_type]?.label}</strong> plan will be canceled and replaced immediately.
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => { setConfirmOpen(false); setChangingPlan(null); }}
                        disabled={submitting}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmChange}
                        disabled={submitting}
                        sx={{
                            fontFamily, fontWeight: 700, textTransform: 'none',
                            bgcolor: changingPlanConfig?.color || GREEN_ACCENT,
                            '&:hover': { bgcolor: changingPlanConfig?.color || GREEN_ACCENT, filter: 'brightness(0.9)' },
                        }}
                    >
                        {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm Change'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    sx={{ fontFamily, borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
